import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Clock4 } from 'lucide-react';
import type { RootState } from '../../../../store/redux/store/store';
import { getCompanyRules, updateCompanyRules } from '../../../../api/General/Company';
import { getUserInfo } from '../../../../api/General/account';
import { getCompanyVariablePayTypes } from '../../../../api/Payroll/variablePay';
import { updateCompanyRules as updateCompanyRulesAction } from '../../../../store/redux/actions/userActionCreator';
import {
  HOURLY_PAY_DEFAULTS,
  HOURLY_ROUNDING_STEPS,
  WEEK_DAY_NAMES,
  type CompanyRulesInput,
  type HourlyRoundingMode,
  type WeekDayName,
} from '../../../../models/general';
import type { CompanyVariablePayTypeView } from '../../../../models/payroll';
import { apiErrorsOf } from '../../../../utils/requestHandling';
import { getApiErrorMessage } from '../../../../utils/apiErrors';
import { Switch } from '../../../../components/ui/Switch';
import { Spinner } from '../../../../components/ui/Spinner';
import StickyActionBar from '../../../../components/ui/StickyActionBar';
import { PageLayout } from '../../../../components/ui/PageLayout';
import AppIcon from '../../../../components/AppIcon';
import { cn } from '../../../../utils/cn';

/**
 * SETTINGS › PAYROLL › HOURLY PAY (docs/hourly-pay-spec.md §3.3, §10).
 *
 * Everything that turns an APPROVED SHIFT into money for an hourly employee: the master switch, the
 * pay-line type, rounding, per-shift minimum/maximum, premium multipliers, the auto-approval rule
 * and the week the approval queue groups by.
 *
 * 🔴 Same round-trip contract as AttendancePaySettings: the POST sends the WHOLE CompanyRulesView
 * back, because CompanyService.UpdateCompanyRules assigns several non-nullable members
 * unconditionally and a partial body would wipe the tenant's attendance configuration.
 *
 * 🔴 NULL MEANS "NOT SENT". Every hourly field is nullable on the wire and a null leaves the stored
 * value alone, so the two fields whose real value is "none" (the per-shift maximum and the premium
 * threshold) are cleared through explicit `clear…` flags, the CO-304 convention. Those flag names
 * are the web lane's assumption — the spec does not name them.
 *
 * ⚠️ Company config is NOT effective-dated. The server snapshots the amount on each shift at
 * decision time (§3.3), so a change here moves only decisions made after it — the copy says so.
 *
 * Controls are native inputs (the shared `Input.jsx` is untyped), with a live rule list under every
 * numeric field, exactly like the overtime settings page.
 */

const INPUT_CLASSES =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm tabular-nums ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ' +
  'transition-colors disabled:cursor-not-allowed disabled:opacity-50';

const SELECT_CLASSES =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ' +
  'transition-colors disabled:cursor-not-allowed disabled:opacity-50';

const ROUNDING_MODES: HourlyRoundingMode[] = ['Nearest', 'Down', 'Up'];

/** Multipliers are bounded client-side to catch typos (15 instead of 1.5). The server stays the authority. */
const MULTIPLIER_MIN = 1;
const MULTIPLIER_MAX = 10;
const TOLERANCE_MAX = 240;

interface HourlyForm {
  enabled: boolean;
  /** '' = automatic ("Hourly Pay" created on enable). */
  variablePayTypeId: string;
  roundingMinutes: number;
  roundingMode: HourlyRoundingMode;
  /** Raw strings so a half-typed value is not coerced while the user types. */
  minPaid: string;
  /** '' = no cap. */
  maxPaid: string;
  restDayMultiplier: string;
  holidayMultiplier: string;
  /** '' = no threshold. */
  thresholdMinutes: string;
  overThresholdMultiplier: string;
  autoApproveEnabled: boolean;
  toleranceMinutes: string;
  weekStartDay: WeekDayName;
}

const isWhole = (raw: string) => /^\d+$/.test(raw.trim());
const num = (raw: string) => Number(raw.trim());

const HourlyPaySettings: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const companyId = useSelector((state: RootState) => state.user.company?.id);

  const [rules, setRules] = useState<CompanyRulesInput | null>(null);
  const [form, setForm] = useState<HourlyForm | null>(null);
  const [baseline, setBaseline] = useState<HourlyForm | null>(null);
  const [payTypes, setPayTypes] = useState<CompanyVariablePayTypeView[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setSaved(false);
    try {
      const [rulesResult, typesResult] = await Promise.all([
        getCompanyRules(companyId),
        getCompanyVariablePayTypes().catch(() => null),
      ]);
      if (rulesResult.errors?.length || !rulesResult.payload) {
        setError(getApiErrorMessage(rulesResult, t, t('hourlyPaySettings.loadFailed')));
        return;
      }
      const p = rulesResult.payload;
      const loaded: HourlyForm = {
        enabled: p.hourlyPayEnabled ?? HOURLY_PAY_DEFAULTS.enabled,
        variablePayTypeId: p.hourlyPayVariablePayTypeId != null ? String(p.hourlyPayVariablePayTypeId) : '',
        roundingMinutes: p.hourlyRoundingMinutes ?? HOURLY_PAY_DEFAULTS.roundingMinutes,
        roundingMode: p.hourlyRoundingMode ?? HOURLY_PAY_DEFAULTS.roundingMode,
        minPaid: String(p.hourlyMinPaidMinutesPerItem ?? HOURLY_PAY_DEFAULTS.minPaidMinutesPerItem),
        maxPaid: p.hourlyMaxPaidMinutesPerItem != null ? String(p.hourlyMaxPaidMinutesPerItem) : '',
        restDayMultiplier: String(p.hourlyRestDayMultiplier ?? HOURLY_PAY_DEFAULTS.restDayMultiplier),
        holidayMultiplier: String(p.hourlyPublicHolidayMultiplier ?? HOURLY_PAY_DEFAULTS.publicHolidayMultiplier),
        thresholdMinutes: p.hourlyThresholdMinutes != null ? String(p.hourlyThresholdMinutes) : '',
        overThresholdMultiplier: String(p.hourlyOverThresholdMultiplier ?? HOURLY_PAY_DEFAULTS.overThresholdMultiplier),
        autoApproveEnabled: p.shiftAutoApproveEnabled ?? HOURLY_PAY_DEFAULTS.autoApproveEnabled,
        toleranceMinutes: String(p.shiftAutoApproveToleranceMinutes ?? HOURLY_PAY_DEFAULTS.autoApproveToleranceMinutes),
        weekStartDay: p.hourlyWeekStartDay ?? HOURLY_PAY_DEFAULTS.weekStartDay,
      };
      setRules(p as unknown as CompanyRulesInput);
      setForm(loaded);
      setBaseline(loaded);
      if (typesResult && !typesResult.errors?.length) setPayTypes(typesResult.payload ?? []);
    } catch (err) {
      setError(getApiErrorMessage(err, t, t('hourlyPaySettings.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [companyId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = useCallback((changes: Partial<HourlyForm>) => {
    setForm((prev) => (prev ? { ...prev, ...changes } : prev));
    setSaved(false);
  }, []);

  const checks = useMemo(() => {
    if (!form) return null;
    const minOk = isWhole(form.minPaid);
    const maxEmpty = form.maxPaid.trim() === '';
    const maxWhole = maxEmpty || (isWhole(form.maxPaid) && num(form.maxPaid) > 0);
    const maxAboveMin = maxEmpty || !minOk || !maxWhole || num(form.maxPaid) >= num(form.minPaid);
    const mult = (raw: string) => {
      const n = Number(raw);
      return raw.trim() !== '' && Number.isFinite(n) && n >= MULTIPLIER_MIN && n <= MULTIPLIER_MAX;
    };
    const thresholdEmpty = form.thresholdMinutes.trim() === '';
    const tolWhole = isWhole(form.toleranceMinutes);
    return {
      minWhole: minOk,
      maxWhole,
      maxAboveMin,
      restDay: mult(form.restDayMultiplier),
      holiday: mult(form.holidayMultiplier),
      thresholdWhole: thresholdEmpty || (isWhole(form.thresholdMinutes) && num(form.thresholdMinutes) > 0),
      overThreshold: thresholdEmpty || mult(form.overThresholdMultiplier),
      toleranceWhole: tolWhole,
      toleranceMax: !tolWhole || num(form.toleranceMinutes) <= TOLERANCE_MAX,
    };
  }, [form]);

  const hasErrors = !!checks && Object.values(checks).some((ok) => !ok);

  const dirty = useMemo(() => {
    if (!form || !baseline) return false;
    return (Object.keys(baseline) as (keyof HourlyForm)[]).some((k) => baseline[k] !== form[k]);
  }, [form, baseline]);

  const handleSave = async () => {
    if (!companyId || !rules || !form || hasErrors) return;
    setSaving(true);
    setError(null);
    try {
      const maxEmpty = form.maxPaid.trim() === '';
      const thresholdEmpty = form.thresholdMinutes.trim() === '';
      const input: CompanyRulesInput = {
        ...rules,
        hourlyPayEnabled: form.enabled,
        hourlyPayVariablePayTypeId: form.variablePayTypeId ? Number(form.variablePayTypeId) : null,
        hourlyRoundingMinutes: form.roundingMinutes,
        hourlyRoundingMode: form.roundingMode,
        hourlyMinPaidMinutesPerItem: num(form.minPaid),
        hourlyMaxPaidMinutesPerItem: maxEmpty ? null : num(form.maxPaid),
        clearHourlyMaxPaidMinutesPerItem: maxEmpty ? true : undefined,
        hourlyRestDayMultiplier: Number(form.restDayMultiplier),
        hourlyPublicHolidayMultiplier: Number(form.holidayMultiplier),
        hourlyThresholdMinutes: thresholdEmpty ? null : num(form.thresholdMinutes),
        clearHourlyThresholdMinutes: thresholdEmpty ? true : undefined,
        hourlyOverThresholdMultiplier: Number(form.overThresholdMultiplier),
        shiftAutoApproveEnabled: form.autoApproveEnabled,
        shiftAutoApproveToleranceMinutes: num(form.toleranceMinutes),
        hourlyWeekStartDay: form.weekStartDay,
      };
      const result = await updateCompanyRules(companyId, input);
      if (result.errors?.length) {
        setError(getApiErrorMessage(result, t, t('hourlyPaySettings.saveFailed')));
        return;
      }
      // The clear flags are per-request instructions, not stored state.
      const persisted: CompanyRulesInput = { ...input };
      delete persisted.clearHourlyMaxPaidMinutesPerItem;
      delete persisted.clearHourlyThresholdMinutes;
      // The server may have filled in the auto-created "Hourly Pay" type on enable.
      const returnedTypeId = result.payload?.hourlyPayVariablePayTypeId;
      if (returnedTypeId != null) persisted.hourlyPayVariablePayTypeId = returnedTypeId;
      const rulesOnly: Partial<CompanyRulesInput> = { ...persisted };
      delete rulesOnly.id;
      delete rulesOnly.companyId;
      dispatch(updateCompanyRulesAction(rulesOnly));
      setRules(persisted);
      const nextForm: HourlyForm = {
        ...form,
        variablePayTypeId: returnedTypeId != null ? String(returnedTypeId) : form.variablePayTypeId,
      };
      setForm(nextForm);
      setBaseline(nextForm);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      // The master switch drives the Shift approvals / My shifts nav, which reads GetUserInfo.
      if (baseline && baseline.enabled !== form.enabled) {
        void getUserInfo().catch(() => undefined);
      }
      if (returnedTypeId != null && !payTypes.some((pt) => pt.id === returnedTypeId)) {
        getCompanyVariablePayTypes()
          .then((r) => {
            if (!r.errors?.length) setPayTypes(r.payload ?? []);
          })
          .catch(() => undefined);
      }
    } catch (err) {
      const described = apiErrorsOf(err)[0]?.description;
      setError(described || getApiErrorMessage(err, t, t('hourlyPaySettings.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (baseline) setForm(baseline);
    setSaved(false);
    setError(null);
  };

  const rule = (ok: boolean, text: string) => (
    <li className="flex items-start gap-1.5">
      <AppIcon name={ok ? 'Check' : 'X'} size={13} className={cn('mt-0.5 shrink-0', ok ? 'text-success-strong' : 'text-muted-foreground')} />
      <span className={cn(ok ? 'text-muted-foreground' : 'text-destructive-strong')}>{text}</span>
    </li>
  );

  const pageTitle = t('hourlyPaySettings.title');
  const pageDescription = t('hourlyPaySettings.description');

  if (loading) {
    return (
      <PageLayout title={pageTitle} description={pageDescription} icon={Clock4} accentColor="blue">
        <div className="flex justify-center rounded-xl border border-border bg-card py-12">
          <Spinner size="lg" />
        </div>
      </PageLayout>
    );
  }

  if (!companyId || !form || !checks) {
    return (
      <PageLayout title={pageTitle} description={pageDescription} icon={Clock4} accentColor="blue">
        <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
          {error || t('hourlyPaySettings.noCompany')}
        </div>
      </PageLayout>
    );
  }

  const off = !form.enabled;
  const locked = saving || off;
  const sectionClass = (extra?: string) =>
    cn('border-t border-border px-5 py-5 transition-opacity duration-200', off && 'opacity-60', extra);

  const numberField = (
    id: string,
    label: string,
    value: string,
    onChange: (v: string) => void,
    ruleList: React.ReactNode,
    opts: { step?: string; placeholder?: string; invalid: boolean; hint?: string; disabled?: boolean; suffix?: string },
  ) => (
    <div>
      <label htmlFor={id} className="text-sm font-medium leading-none text-foreground">
        {label}
      </label>
      <div className="relative mt-2">
        <input
          id={id}
          type="number"
          inputMode="decimal"
          step={opts.step ?? '1'}
          min={0}
          value={value}
          placeholder={opts.placeholder}
          disabled={opts.disabled ?? locked}
          aria-describedby={`${id}-rules`}
          aria-invalid={opts.invalid || undefined}
          onChange={(e) => onChange(e.target.value)}
          className={cn(INPUT_CLASSES, opts.suffix && 'pe-14', opts.invalid && 'border-destructive')}
        />
        {opts.suffix && (
          <span className="pointer-events-none absolute inset-y-0 end-3 flex items-center text-xs text-muted-foreground">
            {opts.suffix}
          </span>
        )}
      </div>
      <ul id={`${id}-rules`} className="mt-2 space-y-1 text-xs">
        {ruleList}
      </ul>
      {opts.hint && <p className="mt-2 max-w-[60ch] text-xs text-muted-foreground">{opts.hint}</p>}
    </div>
  );

  const minutesSuffix = t('hourlyPaySettings.minutesSuffix');

  return (
    <PageLayout title={pageTitle} description={pageDescription} icon={Clock4} accentColor="blue">
      <section aria-labelledby="hourly-pay-heading" className="space-y-4">
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <header className="border-b border-border bg-muted/40 px-5 py-4">
            <h3 id="hourly-pay-heading" className="text-base font-semibold text-foreground">
              {t('hourlyPaySettings.sectionTitle')}
            </h3>
            <p className="mt-1 max-w-[70ch] text-sm text-muted-foreground">{t('hourlyPaySettings.sectionIntro')}</p>
          </header>

          {/* 1 — master switch */}
          <div className={cn('px-5 py-4 transition-colors duration-200', form.enabled ? 'bg-primary/[0.04]' : 'bg-muted/30')}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <label htmlFor="hourly-pay-enabled" className="cursor-pointer text-sm font-semibold text-foreground">
                    {t('hourlyPaySettings.enabledLabel')}
                  </label>
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-[11px] font-medium leading-4',
                      form.enabled ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {form.enabled ? t('hourlyPaySettings.enabledOn') : t('hourlyPaySettings.enabledOff')}
                  </span>
                </div>
                <p className="mt-1.5 max-w-[70ch] text-sm text-muted-foreground">
                  {form.enabled ? t('hourlyPaySettings.enabledOnBody') : t('hourlyPaySettings.enabledOffBody')}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <Link
                    to="/attendance/shift-approvals"
                    className="inline-flex items-center gap-1 text-sm font-medium text-primary underline underline-offset-2"
                  >
                    {t('hourlyPaySettings.queueLink')}
                    <AppIcon name="ArrowRight" size={13} className="rtl:-scale-x-100" />
                  </Link>
                  <Link
                    to="/core-hr/directory"
                    className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground"
                  >
                    {t('hourlyPaySettings.employeesLink')}
                  </Link>
                </div>
              </div>
              <Switch
                id="hourly-pay-enabled"
                checked={form.enabled}
                onCheckedChange={(v) => patch({ enabled: v })}
                disabled={saving}
              />
            </div>
          </div>

          {/* 2 — pay line type + week start */}
          <div className={sectionClass()}>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="hourly-pay-type" className="text-sm font-medium leading-none text-foreground">
                  {t('hourlyPaySettings.payTypeLabel')}
                </label>
                <select
                  id="hourly-pay-type"
                  value={form.variablePayTypeId}
                  disabled={locked}
                  onChange={(e) => patch({ variablePayTypeId: e.target.value })}
                  className={cn(SELECT_CLASSES, 'mt-2')}
                >
                  {baseline?.variablePayTypeId === '' && (
                    <option value="">{t('hourlyPaySettings.payTypeAuto')}</option>
                  )}
                  {payTypes.map((pt) => (
                    <option key={pt.id} value={String(pt.id)}>
                      {pt.name}
                    </option>
                  ))}
                </select>
                <p className="mt-2 max-w-[60ch] text-xs text-muted-foreground">{t('hourlyPaySettings.payTypeHint')}</p>
              </div>
              <div>
                <label htmlFor="hourly-week-start" className="text-sm font-medium leading-none text-foreground">
                  {t('hourlyPaySettings.weekStartLabel')}
                </label>
                <select
                  id="hourly-week-start"
                  value={form.weekStartDay}
                  disabled={locked}
                  onChange={(e) => patch({ weekStartDay: e.target.value as WeekDayName })}
                  className={cn(SELECT_CLASSES, 'mt-2')}
                >
                  {WEEK_DAY_NAMES.map((day) => (
                    <option key={day} value={day}>
                      {t(`hourlyPaySettings.day.${day}`)}
                    </option>
                  ))}
                </select>
                <p className="mt-2 max-w-[60ch] text-xs text-muted-foreground">{t('hourlyPaySettings.weekStartHint')}</p>
              </div>
            </div>
          </div>

          {/* 3 — rounding + per-shift limits */}
          <div className={sectionClass()}>
            <h4 className="text-sm font-semibold text-foreground">{t('hourlyPaySettings.roundingTitle')}</h4>
            <p className="mt-1 max-w-[70ch] text-xs text-muted-foreground">{t('hourlyPaySettings.roundingIntro')}</p>
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <div role="radiogroup" aria-label={t('hourlyPaySettings.roundingStepLabel')} className="inline-flex flex-wrap rounded-md border border-border bg-card p-0.5">
                {HOURLY_ROUNDING_STEPS.map((step) => {
                  const active = form.roundingMinutes === step;
                  return (
                    <button
                      key={step}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      disabled={locked}
                      onClick={() => patch({ roundingMinutes: step })}
                      className={cn(
                        'rounded px-3 py-1.5 text-xs font-medium transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed',
                        active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {step === 0 ? t('hourlyPaySettings.roundingNone') : t('hourlyPaySettings.roundingStep', { n: step })}
                    </button>
                  );
                })}
              </div>
              <div
                role="radiogroup"
                aria-label={t('hourlyPaySettings.roundingModeLabel')}
                className={cn('inline-flex flex-wrap rounded-md border border-border bg-card p-0.5', form.roundingMinutes === 0 && 'opacity-50')}
              >
                {ROUNDING_MODES.map((mode) => {
                  const active = form.roundingMode === mode;
                  return (
                    <button
                      key={mode}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      disabled={locked || form.roundingMinutes === 0}
                      onClick={() => patch({ roundingMode: mode })}
                      className={cn(
                        'rounded px-3 py-1.5 text-xs font-medium transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed',
                        active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {t(`hourlyPaySettings.roundingMode.${mode}`)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              {numberField(
                'hourly-min-paid',
                t('hourlyPaySettings.minPaidLabel'),
                form.minPaid,
                (v) => patch({ minPaid: v }),
                rule(checks.minWhole, t('hourlyPaySettings.ruleWholeMinutes')),
                { invalid: !checks.minWhole, hint: t('hourlyPaySettings.minPaidHint'), suffix: minutesSuffix },
              )}
              {numberField(
                'hourly-max-paid',
                t('hourlyPaySettings.maxPaidLabel'),
                form.maxPaid,
                (v) => patch({ maxPaid: v }),
                <>
                  {rule(checks.maxWhole, t('hourlyPaySettings.ruleEmptyOrPositive'))}
                  {rule(checks.maxAboveMin, t('hourlyPaySettings.ruleMaxAboveMin'))}
                </>,
                {
                  invalid: !checks.maxWhole || !checks.maxAboveMin,
                  placeholder: t('hourlyPaySettings.noCap'),
                  hint: t('hourlyPaySettings.maxPaidHint'),
                  suffix: minutesSuffix,
                },
              )}
            </div>
          </div>

          {/* 4 — premiums */}
          <div className={sectionClass()}>
            <h4 className="text-sm font-semibold text-foreground">{t('hourlyPaySettings.premiumsTitle')}</h4>
            <p className="mt-1 max-w-[70ch] text-xs text-muted-foreground">{t('hourlyPaySettings.premiumsIntro')}</p>
            <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {numberField(
                'hourly-rest-day',
                t('hourlyPaySettings.restDayLabel'),
                form.restDayMultiplier,
                (v) => patch({ restDayMultiplier: v }),
                rule(checks.restDay, t('hourlyPaySettings.ruleMultiplier', { min: MULTIPLIER_MIN, max: MULTIPLIER_MAX })),
                { invalid: !checks.restDay, step: '0.05', suffix: '×' },
              )}
              {numberField(
                'hourly-holiday',
                t('hourlyPaySettings.holidayLabel'),
                form.holidayMultiplier,
                (v) => patch({ holidayMultiplier: v }),
                rule(checks.holiday, t('hourlyPaySettings.ruleMultiplier', { min: MULTIPLIER_MIN, max: MULTIPLIER_MAX })),
                { invalid: !checks.holiday, step: '0.05', suffix: '×', hint: t('hourlyPaySettings.holidayHint') },
              )}
              {numberField(
                'hourly-threshold',
                t('hourlyPaySettings.thresholdLabel'),
                form.thresholdMinutes,
                (v) => patch({ thresholdMinutes: v }),
                rule(checks.thresholdWhole, t('hourlyPaySettings.ruleEmptyOrPositive')),
                {
                  invalid: !checks.thresholdWhole,
                  placeholder: t('hourlyPaySettings.noThreshold'),
                  hint: t('hourlyPaySettings.thresholdHint'),
                  suffix: minutesSuffix,
                },
              )}
              {numberField(
                'hourly-over-threshold',
                t('hourlyPaySettings.overThresholdLabel'),
                form.overThresholdMultiplier,
                (v) => patch({ overThresholdMultiplier: v }),
                rule(checks.overThreshold, t('hourlyPaySettings.ruleMultiplier', { min: MULTIPLIER_MIN, max: MULTIPLIER_MAX })),
                {
                  invalid: !checks.overThreshold,
                  step: '0.05',
                  suffix: '×',
                  disabled: locked || form.thresholdMinutes.trim() === '',
                  hint: form.thresholdMinutes.trim() === '' ? t('hourlyPaySettings.overThresholdInert') : undefined,
                },
              )}
            </div>
            <p className="mt-4 flex items-start gap-1.5 text-xs text-muted-foreground">
              <AppIcon name="Info" size={13} className="mt-0.5 shrink-0" />
              <span className="max-w-[70ch]">{t('hourlyPaySettings.snapshotNote')}</span>
            </p>
          </div>

          {/* 5 — auto-approval (D7) */}
          <div className={sectionClass()}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <label htmlFor="hourly-auto-approve" className="cursor-pointer text-sm font-semibold text-foreground">
                  {t('hourlyPaySettings.autoApproveLabel')}
                </label>
                <p className="mt-1.5 max-w-[70ch] text-sm text-muted-foreground">{t('hourlyPaySettings.autoApproveBody')}</p>
              </div>
              <Switch
                id="hourly-auto-approve"
                checked={form.autoApproveEnabled}
                onCheckedChange={(v) => patch({ autoApproveEnabled: v })}
                disabled={locked}
              />
            </div>
            <div className="mt-4 grid gap-5 sm:grid-cols-2">
              {numberField(
                'hourly-tolerance',
                t('hourlyPaySettings.toleranceLabel'),
                form.toleranceMinutes,
                (v) => patch({ toleranceMinutes: v }),
                <>
                  {rule(checks.toleranceWhole, t('hourlyPaySettings.ruleWholeMinutes'))}
                  {rule(checks.toleranceMax, t('hourlyPaySettings.ruleToleranceMax', { max: TOLERANCE_MAX }))}
                </>,
                {
                  invalid: !checks.toleranceWhole || !checks.toleranceMax,
                  hint: t('hourlyPaySettings.toleranceHint'),
                  suffix: minutesSuffix,
                },
              )}
            </div>
          </div>
        </div>

        {error && (
          <div role="alert" className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive-strong">
            <AppIcon name="AlertCircle" size={16} className="mt-0.5 shrink-0" />
            <span className="whitespace-pre-line">{error}</span>
          </div>
        )}

        {dirty && hasErrors && (
          <p className="flex items-center gap-1.5 text-sm text-destructive-strong">
            <AppIcon name="AlertCircle" size={15} className="shrink-0" />
            {t('hourlyPaySettings.blockedBySelf')}
          </p>
        )}

        {saved && (
          <p className="flex items-center gap-1.5 text-sm text-success-strong" role="status">
            <AppIcon name="Check" size={15} className="shrink-0" />
            {t('hourlyPaySettings.saved')}
          </p>
        )}

        <StickyActionBar
          hasUnsavedChanges={dirty && !hasErrors}
          isSaving={saving}
          onReset={handleReset}
          onSave={() => void handleSave()}
          t={t as (key: string) => string}
          saveLabel={t('hourlyPaySettings.save')}
          saveTestId="hourly-pay-save"
          resetTestId="hourly-pay-reset"
        />
      </section>
    </PageLayout>
  );
};

export default HourlyPaySettings;
